/**
 * Measuring tools: geodesic segment with its length, triangle with its angle sum and area, circle
 * with its circumference. Points live in (anchored) world coordinates.
 */
import type { Complex } from '../geometry/complex';
import {
  circleArea,
  circleCircumference,
  circleLengthNumeric,
  distance,
  triangleAreaNumeric,
  triangleMeasures,
} from '../geometry/hyperbolic';
import { type Isometry, apply } from '../geometry/mobius';

export type MeasureKind = 'geodesic' | 'triangle' | 'circle';

export const POINTS_NEEDED: Record<MeasureKind, number> = { geodesic: 2, triangle: 3, circle: 2 };

export type Readout =
  | { kind: 'geodesic'; distance: number }
  | {
      kind: 'triangle';
      angles: [number, number, number];
      sum: number;
      area: number;
      integrated: number;
    }
  | {
      kind: 'circle';
      radius: number;
      circumference: number;
      measured: number;
      euclidean: number;
      area: number;
    };

export class Measurement {
  kind: MeasureKind;
  points: Complex[] = [];

  constructor(kind: MeasureKind) {
    this.kind = kind;
  }

  get complete(): boolean {
    return this.points.length >= POINTS_NEEDED[this.kind];
  }

  get needed(): number {
    return POINTS_NEEDED[this.kind];
  }

  add(p: Complex): void {
    if (this.complete) this.points = [];
    this.points.push(p);
  }

  move(i: number, p: Complex): void {
    if (i >= 0 && i < this.points.length) this.points[i] = p;
  }

  transform(g: Isometry): void {
    this.points = this.points.map((p) => apply(g, p));
  }

  readout(): Readout | null {
    if (!this.complete) return null;
    const [a, b, c] = this.points;
    if (this.kind === 'geodesic') return { kind: 'geodesic', distance: distance(a, b) };
    if (this.kind === 'triangle') {
      const m = triangleMeasures(a, b, c);
      return {
        kind: 'triangle',
        angles: [m.alpha, m.beta, m.gamma],
        sum: m.sum,
        area: m.area,
        integrated: m.area > 1e-9 ? triangleAreaNumeric(a, b, c, 1e-10) : 0,
      };
    }
    const r = distance(a, b);
    return {
      kind: 'circle',
      radius: r,
      circumference: circleCircumference(r),
      measured: circleLengthNumeric(a, r),
      euclidean: 2 * Math.PI * r,
      area: circleArea(r),
    };
  }
}

const deg = (x: number) => (x * 180) / Math.PI;
export const fmt = (x: number, digits = 3): string =>
  Number.isFinite(x) ? x.toFixed(digits) : '∞';
export const fmtDeg = (x: number): string => `${deg(x).toFixed(1)}°`;
